<template>
	<section v-if="hasAnyMedia" class="teacher-media">
		<h3 class="section-title">{{ t(`${I18N_NS}.sections.media`) }}</h3>

		<input
			id="teacher-media-tab-video"
			class="teacher-media__tab-input"
			type="radio"
			name="teacher-media-tab"
			:checked="hasVideoContent"
		/>
		<input
			id="teacher-media-tab-article"
			class="teacher-media__tab-input"
			type="radio"
			name="teacher-media-tab"
			:checked="!hasVideoContent && articleItems.length > 0"
		/>

		<div class="teacher-media__filters">
			<label
				v-for="group in mediaGroups"
				:key="group.key"
				class="teacher-media__filter"
				:class="`teacher-media__filter--${group.key}`"
				:for="`teacher-media-tab-${group.key}`"
				@click="trackMediaTab(group.key)"
			>
				<span>{{ group.label }}</span>
				<span>{{ group.count }}</span>
			</label>
		</div>

		<div class="teacher-media__panels">
			<div class="teacher-media__panel teacher-media__panel--video">
				<div v-if="hasVideoContent" class="teacher-media__content">
					<section v-if="portraitVideos.length" class="portrait-video">
						<div class="portrait-video__grid">
							<article
								v-for="(item, index) in visiblePortraitVideos"
								:key="`portrait-${item.id}`"
								:ref="el => bindMediaImpression(el, item, index)"
								class="portrait-card"
								@click="handleToDetail(item, index)"
							>
								<img
									class="portrait-card__cover"
									:src="item.cover || defaultCover"
									:alt="item.title"
								/>
								<div class="portrait-card__shade"></div>
								<span class="play-badge play-badge--large" aria-hidden="true"></span>
								<h4 class="portrait-card__title">{{ item.title }}</h4>
							</article>
						</div>

						<div v-if="portraitPageCount > 1" class="portrait-video__nav">
							<button
								type="button"
								class="circle-btn"
								:aria-label="t(`${I18N_NS}.prevPage`)"
								@click="goPortraitPage(-1)"
							>
								<span class="chevron chevron--left" aria-hidden="true"></span>
							</button>
							<div class="portrait-video__dots" aria-hidden="true">
								<span
									v-for="pageNumber in portraitPageCount"
									:key="pageNumber"
									:class="{ 'is-active': portraitPage === pageNumber }"
								></span>
							</div>
							<button
								type="button"
								class="circle-btn"
								:aria-label="t(`${I18N_NS}.nextPage`)"
								@click="goPortraitPage(1)"
							>
								<span class="chevron chevron--right" aria-hidden="true"></span>
							</button>
						</div>
					</section>

					<section v-if="landscapeVideos.length" class="landscape-video">
						<div class="media-list-grid">
							<article
								v-for="(item, index) in visibleLandscapeVideos"
								:key="`landscape-${item.id}`"
								:ref="el => bindMediaImpression(el, item, index)"
								class="landscape-card"
								@click="handleToDetail(item, index)"
							>
								<div class="landscape-card__cover-wrap">
									<img
										class="landscape-card__cover"
										:src="item.cover || defaultCover"
										:alt="item.title"
									/>
									<span class="play-badge play-badge--corner" aria-hidden="true"></span>
									<span v-if="item.duration" class="landscape-card__duration">
										{{ item.duration }}
									</span>
								</div>
								<h4 class="landscape-card__title">{{ item.title }}</h4>
								<div class="landscape-card__meta">
									<img :src="eyeIcon" alt="" aria-hidden="true" />
									<span>{{ t(`${I18N_NS}.mediaViewCount`, { count: item.viewCount }) }}</span>
								</div>
							</article>
						</div>

						<PaginationControls
							v-if="landscapePageCount > 1"
							:page="landscapePage"
							:page-count="landscapePageCount"
							@change="setLandscapePage"
						/>
					</section>
				</div>

				<div v-else class="teacher-media__empty">{{ t(`${I18N_NS}.mediaVideoEmpty`) }}</div>
			</div>

			<div class="teacher-media__panel teacher-media__panel--article">
				<div v-if="articleItems.length" class="teacher-media__content">
					<div class="article-grid">
						<article
							v-for="(item, index) in visibleArticles"
							:key="`article-${item.id}`"
							:ref="el => bindMediaImpression(el, item, index)"
							class="article-card"
							@click="handleToDetail(item, index)"
						>
							<img
								class="article-card__cover"
								:src="item.cover || defaultCover"
								:alt="item.title"
							/>
							<div class="article-card__shade"></div>
							<h4 class="article-card__title">{{ item.title }}</h4>
						</article>
					</div>

					<PaginationControls
						v-if="articlePageCount > 1"
						:page="articlePage"
						:page-count="articlePageCount"
						@change="setArticlePage"
					/>
				</div>

				<div v-else class="teacher-media__empty">{{ t(`${I18N_NS}.mediaArticleEmpty`) }}</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, ref, watch } from "vue";
import { playContent } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { buildOssImageUrl } from "~/utils/oss-image";
import {
	resolveMediaTypeByItem,
	useInjectedMentorDetailTracking,
} from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultCover from "~/features/page-variants/seer/square/images/video_view.png";
import eyeIcon from "~/features/page-variants/seer/square/images/eye.png";

const I18N_NS = "teacherDetail.pc";
const PORTRAIT_PAGE_SIZE = 5;
const LIST_PAGE_SIZE = 6;
const ARTICLE_PAGE_SIZE = 15;
const { t, locale } = useI18n();
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();
const previousMediaTab = ref("video");

const props = defineProps({
	data: {
		type: Object,
		default: () => ({
			horizVideo: [],
			vertVideo: [],
			articleList: [],
		}),
	},
});

const portraitPage = ref(1);
const landscapePage = ref(1);
const articlePage = ref(1);

const portraitVideos = computed(() => dedupeById(props.data?.vertVideo || []).map(formatVideo));
const landscapeVideos = computed(() => dedupeById(props.data?.horizVideo || []).map(formatVideo));
const articleItems = computed(() => dedupeById(props.data?.articleList || []).map(formatArticle));

const videoCount = computed(() => portraitVideos.value.length + landscapeVideos.value.length);
const hasVideoContent = computed(() => videoCount.value > 0);
const hasAnyMedia = computed(() => hasVideoContent.value || articleItems.value.length > 0);

const portraitPageCount = computed(() => getPageCount(portraitVideos.value, PORTRAIT_PAGE_SIZE));
const landscapePageCount = computed(() => getPageCount(landscapeVideos.value, LIST_PAGE_SIZE));
const articlePageCount = computed(() => getPageCount(articleItems.value, ARTICLE_PAGE_SIZE));

const visiblePortraitVideos = computed(() =>
	getPagedItems(portraitVideos.value, portraitPage.value, PORTRAIT_PAGE_SIZE)
);
const visibleLandscapeVideos = computed(() =>
	getPagedItems(landscapeVideos.value, landscapePage.value, LIST_PAGE_SIZE)
);
const visibleArticles = computed(() =>
	getPagedItems(articleItems.value, articlePage.value, ARTICLE_PAGE_SIZE)
);

const mediaGroups = computed(() => [
	{
		key: "video",
		label: t(`${I18N_NS}.mediaVideo`),
		count: videoCount.value,
		emptyText: t(`${I18N_NS}.mediaVideoEmpty`),
	},
	{
		key: "article",
		label: t(`${I18N_NS}.mediaArticle`),
		count: articleItems.value.length,
		emptyText: t(`${I18N_NS}.mediaArticleEmpty`),
	},
]);

watch(portraitPageCount, count => {
	portraitPage.value = Math.min(Math.max(portraitPage.value, 1), Math.max(count, 1));
});
watch(landscapePageCount, count => {
	landscapePage.value = Math.min(Math.max(landscapePage.value, 1), Math.max(count, 1));
});
watch(articlePageCount, count => {
	articlePage.value = Math.min(Math.max(articlePage.value, 1), Math.max(count, 1));
});

const PaginationControls = defineComponent({
	name: "PaginationControls",
	props: {
		page: {
			type: Number,
			required: true,
		},
		pageCount: {
			type: Number,
			required: true,
		},
	},
	emits: ["change"],
	setup(componentProps, { emit }) {
		const setPage = pageNumber => {
			const nextPage = Math.min(Math.max(pageNumber, 1), componentProps.pageCount);
			if (nextPage !== componentProps.page) {
				emit("change", nextPage);
			}
		};

		return () =>
			h("div", { class: "media-pagination" }, [
				h(
					"button",
					{
						type: "button",
						class: "media-pagination__btn",
						disabled: componentProps.page <= 1,
						onClick: () => setPage(componentProps.page - 1),
					},
					[h("span", { class: "chevron chevron--left", "aria-hidden": "true" })]
				),
				...Array.from({ length: componentProps.pageCount }, (_, index) => {
					const pageNumber = index + 1;
					return h(
						"button",
						{
							type: "button",
							class: [
								"media-pagination__page",
								{ "media-pagination__page--active": componentProps.page === pageNumber },
							],
							onClick: () => setPage(pageNumber),
						},
						String(pageNumber)
					);
				}),
				h(
					"button",
					{
						type: "button",
						class: "media-pagination__btn",
						disabled: componentProps.page >= componentProps.pageCount,
						onClick: () => setPage(componentProps.page + 1),
					},
					[h("span", { class: "chevron chevron--right", "aria-hidden": "true" })]
				),
			]);
	},
});

function formatVideo(item) {
	return {
		...item,
		kind: "video",
		isVideo: true,
		title: pickLocalizedTitle(item),
		cover: pickCover(item),
		duration: item.media_second_str || formatTime(item.media_second),
		viewCount: formatViewCount(item.view_count ?? item.media_play_count),
	};
}

function formatArticle(item) {
	return {
		...item,
		kind: "article",
		isVideo: false,
		title: pickLocalizedTitle(item),
		cover: pickCover(item),
		duration: "",
		viewCount: formatViewCount(item.view_count ?? item.media_play_count),
	};
}

function pickLocalizedTitle(item) {
	const isEn = locale.value === "en";
	return (isEn ? item.title_en : item.title) || item.title || "";
}

function pickCover(item) {
	const url = item.cover || item.cover_url || item.image || item.image_url || item.thumb || "";
	return buildOssImageUrl(url, { strategy: "cover", w: 640, q: 80 });
}

function formatViewCount(value) {
	const count = Number(value) || 0;
	return count.toLocaleString();
}

function formatTime(seconds) {
	const value = Number(seconds) || 0;
	if (!value) return "";
	const mins = Math.floor(value / 60)
		.toString()
		.padStart(2, "0");
	const secs = Math.floor(value % 60)
		.toString()
		.padStart(2, "0");
	return `${mins}:${secs}`;
}

function getPageCount(list, pageSize) {
	return Math.ceil(list.length / pageSize) || 0;
}

function getPagedItems(list, page, pageSize) {
	const start = (page - 1) * pageSize;
	return list.slice(start, start + pageSize);
}

function setLandscapePage(page) {
	tracking?.track?.("mentor_media_pagination_click", {
		media_type: "video",
		page_index: page,
		direction: page > landscapePage.value ? "next" : "prev",
	});
	landscapePage.value = page;
}

function setArticlePage(page) {
	tracking?.track?.("mentor_media_pagination_click", {
		media_type: "article",
		page_index: page,
		direction: page > articlePage.value ? "next" : "prev",
	});
	articlePage.value = page;
}

function goPortraitPage(step) {
	if (!portraitPageCount.value) return;
	const nextPage = portraitPage.value + step;
	tracking?.track?.("mentor_media_pagination_click", {
		media_type: "video",
		page_index: nextPage,
		direction: step > 0 ? "next" : "prev",
	});
	if (nextPage < 1) {
		portraitPage.value = portraitPageCount.value;
		return;
	}
	if (nextPage > portraitPageCount.value) {
		portraitPage.value = 1;
		return;
	}
	portraitPage.value = nextPage;
}

function trackMediaTab(mediaType) {
	if (previousMediaTab.value === mediaType) return;
	tracking?.track?.("mentor_media_tab_click", {
		media_type: mediaType,
		content_count: mediaType === "article" ? articleItems.value.length : videoCount.value,
	});
	previousMediaTab.value = mediaType;
}

function bindMediaImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	const mediaType = resolveMediaTypeByItem(item);
	impressionTracker.bind(el, `media_${mediaType}_${item.id}`, "mentor_media_card_impression", {
		content_id: String(item.id),
		media_type: mediaType,
		position: index + 1,
	});
}

function dedupeById(list) {
	const usedIds = new Set();
	return list.filter(item => {
		const id = item?.id;
		if (!id) return true;
		const normalizedId = String(id);
		if (usedIds.has(normalizedId)) return false;
		usedIds.add(normalizedId);
		return true;
	});
}

async function handleToDetail(item, index) {
	if (!item?.id) return;
	tracking?.track?.("mentor_media_card_click", {
		content_id: String(item.id),
		media_type: resolveMediaTypeByItem(item),
		position: typeof index === "number" ? index + 1 : undefined,
	});
	if (item.isVideo) {
		playContent({ square_id: item.id }).catch(() => {});
	}
	await navigateTo(`/seer/square-detail/${item.id}`);
}

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.section-title {
	margin: 0 0 28px;
	color: #333;
	font-size: 24px;
	font-weight: 500;
	line-height: 1.3;
}

.teacher-media {
	font-family: "Source Han Serif CN", "Source Han Serif CN";

	&__tab-input {
		position: absolute;
		width: 1px;
		height: 1px;
		opacity: 0;
		pointer-events: none;
	}

	&__filters {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
		margin-bottom: 28px;
	}

	&__filter {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 2px;
		min-width: 90px;
		height: 40px;
		padding: 0 18px;
		border: 0;
		border-radius: 6px;
		background: #f6f6f6;
		color: #a0a0a0;
		font-size: 16px;
		font-weight: 500;
		cursor: pointer;

		&:hover {
			background: #e5e5e5;
		}
	}

	&__panel {
		display: none;
	}

	#teacher-media-tab-video:checked ~ &__filters &__filter--video,
	#teacher-media-tab-article:checked ~ &__filters &__filter--article {
		border: 1px solid #333333;
		background: #ffffff;
		color: #333333;
	}

	#teacher-media-tab-video:checked ~ &__panels &__panel--video,
	#teacher-media-tab-article:checked ~ &__panels &__panel--article {
		display: block;
	}

	&__content {
		min-width: 0;
	}

	&__empty {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 160px;
		border: 1px dashed #dedede;
		border-radius: 8px;
		color: #999999;
		font-size: 15px;
	}
}

.portrait-video {
	margin-bottom: 32px;

	&__grid {
		display: grid;
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: 24px;
	}

	&__nav {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-top: 18px;
	}

	&__dots {
		display: inline-flex;
		align-items: center;
		gap: 8px;

		span {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: #dedede;

			&.is-active {
				background: #e7b84b;
			}
		}
	}
}

.portrait-card {
	position: relative;
	min-width: 0;
	aspect-ratio: 230 / 335;
	border-radius: 8px;
	overflow: hidden;
	background: #eeeeee;
	cursor: pointer;

	&:hover &__cover {
		transform: scale(1.04);
	}

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.25s ease;
	}

	&__shade {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0) 0%,
			rgba(0, 0, 0, 0) 55%,
			rgba(0, 0, 0, 0.72) 100%
		);
		pointer-events: none;
	}

	&__title {
		position: absolute;
		left: 12px;
		right: 12px;
		bottom: 14px;
		margin: 0;
		color: #ffffff;
		font-size: 15px;
		font-weight: 500;
		line-height: 1.45;
		@include line-clamp(2);
	}
}

.landscape-video {
	margin-top: 28px;
}

.media-list-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 24px;
}

.article-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 24px;
}

.article-card {
	position: relative;
	min-width: 0;
	aspect-ratio: 230 / 335;
	border-radius: 8px;
	overflow: hidden;
	background: #eeeeee;
	cursor: pointer;

	&:hover &__cover {
		transform: scale(1.04);
	}

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.25s ease;
	}

	&__shade {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0) 0%,
			rgba(0, 0, 0, 0) 52%,
			rgba(0, 0, 0, 0.76) 100%
		);
		pointer-events: none;
	}

	&__title {
		position: absolute;
		left: 12px;
		right: 12px;
		bottom: 14px;
		margin: 0;
		color: #ffffff;
		font-size: 15px;
		font-weight: 500;
		line-height: 1.45;
		@include line-clamp(2);
	}
}

.landscape-card {
	min-width: 0;
	cursor: pointer;

	&:hover &__cover {
		transform: scale(1.03);
	}

	&__cover-wrap {
		position: relative;
		aspect-ratio: 400 / 224;
		border-radius: 6px;
		overflow: hidden;
		background: #eeeeee;
	}

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.25s ease;
	}

	&__duration {
		position: absolute;
		right: 6px;
		bottom: 6px;
		padding: 2px 6px;
		border-radius: 3px;
		background: rgba(0, 0, 0, 0.65);
		color: #ffffff;
		font-size: 12px;
		line-height: 1.35;
	}

	&__title {
		margin: 12px 0 8px;
		color: #333333;
		font-size: 16px;
		font-weight: 500;
		line-height: 1.5;
		@include line-clamp(1);
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		color: #595959;
		font-size: 12px;
		line-height: 1.4;

		img {
			width: 14px;
			height: 14px;
			opacity: 0.8;
		}
	}
}

.play-badge {
	position: absolute;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.46);
	pointer-events: none;

	&::before {
		content: "";
		width: 0;
		height: 0;
		margin-left: 2px;
		border-top: 7px solid transparent;
		border-bottom: 7px solid transparent;
		border-left: 10px solid #ffffff;
	}

	&--large {
		left: 50%;
		top: 50%;
		width: 42px;
		height: 42px;
		transform: translate(-50%, -50%);
	}

	&--corner {
		top: 8px;
		right: 8px;
		width: 28px;
		height: 28px;

		&::before {
			border-top-width: 5px;
			border-bottom-width: 5px;
			border-left-width: 8px;
		}
	}
}

.circle-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	border: 1px solid #f0f0f0;
	border-radius: 50%;
	background: #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	cursor: pointer;

	&:hover {
		background: #e5e5e5;
	}
}

.chevron {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;
	color: #666666;

	&--left {
		transform: rotate(-135deg);
		margin-left: 3px;
	}

	&--right {
		transform: rotate(45deg);
		margin-right: 3px;
	}
}

:deep(.media-pagination) {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin-top: 42px;
}

:deep(.media-pagination__btn),
:deep(.media-pagination__page) {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid #eeeeee;
	border-radius: 8px;
	background: #ffffff;
	color: #333333;
	font-size: 16px;
	cursor: pointer;

	&:hover {
		background: #e5e5e5;
	}

	&:disabled {
		border-color: #eeeeee;
		background: #eeeeee;
		color: #c8c8c8;
		cursor: not-allowed;
	}
}

:deep(.media-pagination__page--active) {
	border-color: #333333;
	background: #333333;
	color: #ffffff;

	&:hover {
		opacity: 0.7;
		background: #333333;
	}
}

:deep(.media-pagination__btn .chevron) {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;
}

:deep(.media-pagination__btn .chevron--left) {
	margin-left: 3px;
	transform: rotate(-135deg);
}

:deep(.media-pagination__btn .chevron--right) {
	margin-right: 3px;
	transform: rotate(45deg);
}

@media (max-width: 1024px) {
	.portrait-video {
		&__grid {
			grid-template-columns: repeat(4, minmax(0, 1fr));
			gap: 16px;
		}
	}

	.article-grid {
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 20px;
	}

	.media-list-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 20px;
	}
}

@media (max-width: 640px) {
	.portrait-video {
		&__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	.article-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.media-list-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 420px) {
	.portrait-video {
		&__grid {
			grid-template-columns: 1fr;
		}
	}

	.article-grid {
		grid-template-columns: 1fr;
	}
}
</style>
